import type { BenchScene } from "./scenes";
import type { EngineRunner } from "./runner";
import { createFast2DRunner } from "./fast2dRunner";
import { createMatterRunner } from "./matterRunner";
import { createPlanckRunner } from "./planckRunner";
import { createP2Runner } from "./p2Runner";

/** One competitor in the benchmark field. */
export interface EngineDef {
	id: string;
	name: string;
	/** Brand hue used for the live render and the leaderboard. */
	hue: number;
	/** Temporal substeps per displayed simulation step at default settings. */
	substeps: number;
	/**
	 * Approximate internal solver iterations (velocity + position sweeps) each
	 * engine runs *per substep* at its out-of-the-box defaults. Fast2D does one
	 * soft solve + relax per substep; Matter/Planck/p2 take a single step but
	 * loop ~10–11 Gauss–Seidel iterations inside it.
	 */
	iterationsPerSubstep: number;
	/** True when `iterationsPerSubstep` is an approximation of the engine's internals. */
	approxIterations?: boolean;
	/** Fast2D itself — highlighted in the UI. */
	primary?: boolean;
	create: (scene: BenchScene) => EngineRunner;
}

/** The field: Fast2D plus the popular JavaScript 2D physics engines. */
export const engines: EngineDef[] = [
	{
		id: "fast2d",
		name: "Fast2D",
		hue: 265,
		substeps: 4,
		iterationsPerSubstep: 1,
		primary: true,
		create: createFast2DRunner,
	},
	{
		id: "matter",
		name: "Matter.js",
		hue: 28,
		substeps: 1,
		iterationsPerSubstep: 10,
		approxIterations: true,
		create: createMatterRunner,
	},
	{
		id: "planck",
		name: "Planck.js",
		hue: 150,
		substeps: 1,
		iterationsPerSubstep: 11,
		approxIterations: true,
		create: createPlanckRunner,
	},
	{
		id: "p2",
		name: "p2.js",
		hue: 200,
		substeps: 1,
		iterationsPerSubstep: 10,
		approxIterations: true,
		create: createP2Runner,
	},
];
